import { lazy, Suspense } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useBoardDetail } from '@/entities/board';
import { useCard } from '@/entities/card';
import { Dialog, ErrorState, LoadingState } from '@/shared/ui';

const CardDialog = lazy(() =>
  import('@/features/card-details').then((module) => ({ default: module.CardDialog })),
);

/**
 * `/app/workspaces/:workspaceId/calendar/cards/:cardId`: a card opened from the workspace
 * calendar. The card dialog needs its board (columns, labels), which is loaded from the card.
 */
export function WorkspaceCardRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId = '', cardId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const card = useCard(cardId);
  const board = useBoardDetail(card.data?.placement.board_id ?? '');
  const close = () =>
    void navigate({
      pathname: `/app/workspaces/${workspaceId}/calendar`,
      search: searchParams.toString(),
    });

  if (card.isError || board.isError) {
    return (
      <Dialog title={t('calendar.pageTitle')} onClose={close}>
        <ErrorState error={card.error ?? board.error} />
        <Link to={`/app/workspaces/${workspaceId}`}>{t('common.backHome')}</Link>
      </Dialog>
    );
  }
  if (!board.data) return <LoadingState />;
  return (
    <Suspense fallback={<LoadingState />}>
      <CardDialog key={cardId} cardId={cardId} board={board.data} onClose={close} />
    </Suspense>
  );
}
